import { ref, computed } from 'vue';
import reportsService from '@/services/reports/reports.service';
import { useToast } from '@/composables/useToast';

export interface WeeklyBillingCustomer {
  customer: {
    id: string;
    accountNumber: string;
    accountName: string;
  };
  status: {
    code: 'finalized' | 'pending';
    description: string;
  };
  destination: string;
}

export interface WeeklyBillingDestination {
  code: string;
  count: number;
}

export interface WeeklyBillingFinalizePayload {
  customer: string[];
  destination: string[];
  dateRange: {
    start: string;
    end: string;
  };
}

export interface WeeklyBillingApiResponse<T> {
  message: string;
  data: T;
}

export const getStatusColor = (statusCode: 'finalized' | 'pending') => {
  switch (statusCode) {
    case 'finalized':
      return {
        color: 'success',
        variant: 'elevated',
        icon: 'mdi-check-circle',
      };
    case 'pending':
      return {
        color: 'warning',
        variant: 'elevated',
        icon: 'mdi-clock-outline',
      };
    default:
      return {
        color: 'default',
        variant: 'elevated',
        icon: 'mdi-help-circle',
      };
  }
};

export const getDestinationColor = (code: string) => {
  const colors = ['blue', 'purple', 'indigo', 'pink', 'cyan', 'orange', 'teal', 'red'];

  let hash = 0;
  for (let i = 0; i < code.length; i++) {
    hash = code.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;

  return {
    color: colors[index],
    variant: 'elevated',
    icon: 'mdi-map-marker',
  };
};

export function useWeeklyBilling() {
  const toast = useToast();

  const rawCustomers = ref<WeeklyBillingCustomer[]>([]);
  const customers = ref<WeeklyBillingCustomer[]>([]);
  const destinations = ref<WeeklyBillingDestination[]>([]);
  const selectedCustomers = ref<WeeklyBillingCustomer[]>([]);
  const selectedDestinations = ref<(string | WeeklyBillingDestination)[]>([]);
  const searchInput = ref('');

  const isLoadingCustomers = ref(false);
  const isLoadingDestinations = ref(false);
  const isDownloading = ref(false);
  const isFinalizing = ref(false);
  const hasShownError = ref(false);

  const hasSelectedData = computed(() => selectedCustomers.value.length > 0);

  const filteredCustomers = computed(() => {
    if (!rawCustomers.value || rawCustomers.value.length === 0) {
      return [];
    }

    const destinationsNotSelected =
      !selectedDestinations.value || selectedDestinations.value.length === 0;
    if (destinationsNotSelected && !searchInput.value) {
      return rawCustomers.value;
    }

    const filteredByDestination = destinationsNotSelected
      ? rawCustomers.value
      : rawCustomers.value.filter((customer) => {
          return selectedDestinations.value.some((dest) =>
            typeof dest === 'string'
              ? dest === customer.destination
              : dest.code === customer.destination
          );
        });

    const finalResult = searchInput.value
      ? filteredByDestination.filter((el) => {
          const accNameMatch = el.customer.accountName
            .toLowerCase()
            .includes(searchInput.value.toLowerCase());
          const accNumberMatch = el.customer.accountNumber
            .toLowerCase()
            .includes(searchInput.value.toLowerCase());
          return accNameMatch || accNumberMatch;
        })
      : filteredByDestination;

    selectedCustomers.value = [];
    return finalResult;
  });

  const handleError = (error: any, defaultMessage: string) => {
    if (hasShownError.value) {
      return false;
    }

    const isNoChargeCodeError =
      (error?.response?.status === 500 &&
        (error?.response?.data?.includes('No charge code found') ||
          error?.response?.data?.includes('Error reason: No charge code found!') ||
          error?.message?.includes('No charge code found'))) ||
      error?.response?.data === 'Error reason: No charge code found!' ||
      error?.message === 'Error reason: No charge code found!';

    if (isNoChargeCodeError) {
      toast.error(
        'IPI Weekly Billing is not configured. Please contact administrator to set up charge codes.'
      );
    } else {
      toast.error(defaultMessage);
    }

    hasShownError.value = true;
    return isNoChargeCodeError;
  };

  const fetchCustomers = async (dateRange: { start: string; end: string }) => {
    try {
      isLoadingCustomers.value = true;
      const response = await reportsService.getWeeklyBillingCustomers(dateRange);
      if (response.data && Array.isArray(response.data)) {
        rawCustomers.value = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        rawCustomers.value = response.data.data;
      } else {
        rawCustomers.value = [];
      }

      const customerMap = new Map<string, WeeklyBillingCustomer>();

      rawCustomers.value.forEach((customer) => {
        const customerId = customer.customer.id;
        const existingCustomer = customerMap.get(customerId);

        if (!existingCustomer) {
          customerMap.set(customerId, customer);
        } else {
          if (customer.status.code === 'finalized' && existingCustomer.status.code === 'pending') {
            customerMap.set(customerId, customer);
          }
        }
      });

      rawCustomers.value = rawCustomers.value.map((el) => ({
        ...el,
        _uiid: `${el.customer.id}-${el.destination}`,
      }));

      customers.value = Array.from(customerMap.values());
      selectedCustomers.value = [];
    } catch (error: any) {
      handleError(error, 'Failed to fetch customers');
      customers.value = [];
      selectedCustomers.value = [];
    } finally {
      isLoadingCustomers.value = false;
    }
  };

  const fetchDestinations = async (dateRange: { start: string; end: string }) => {
    try {
      isLoadingDestinations.value = true;
      const response = await reportsService.getWeeklyBillingDestinations(dateRange);
      let destinationsData: WeeklyBillingDestination[] = [];
      if (response.data && Array.isArray(response.data)) {
        destinationsData = response.data;
      } else if (response.data && response.data.data) {
        if (Array.isArray(response.data.data)) {
          destinationsData = response.data.data;
        } else if (typeof response.data.data === 'object') {
          destinationsData = Object.entries(response.data.data).map(([code, count]) => ({
            code,
            count: count as number,
          }));
        } else {
          destinationsData = [];
        }
      } else {
        destinationsData = [];
      }

      destinations.value = destinationsData;
    } catch (error: any) {
      handleError(error, 'Failed to fetch destinations');
      destinations.value = [];
      selectedDestinations.value = [];
    } finally {
      isLoadingDestinations.value = false;
    }
  };

  const downloadReport = async (dateRange: { start: string; end: string }) => {
    try {
      isDownloading.value = true;
      hasShownError.value = false;

      const destinationsFromSelectedCustomers = [
        ...new Set(selectedCustomers.value.map((c) => c.destination)),
      ];

      const payload = {
        dateRange,
        customers: selectedCustomers.value.map((c) => c.customer.accountNumber),
        destinations: destinationsFromSelectedCustomers,
      };

      const response = await reportsService.downloadWeeklyBillingReport(payload);

      if (!response?.data) {
        return;
      }

      const { base64Data, filename } = response.data;

      if (!base64Data) {
        return;
      }

      const byteCharacters = atob(base64Data.split(',')[1]);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download =
        filename ||
        `IPI_Weekly_Billing_${dateRange.start.split('T')[0]}_${dateRange.end.split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully');
    } catch (error: any) {
      handleError(error, 'Failed to download report');
    } finally {
      isDownloading.value = false;
    }
  };

  const finalizeCharges = async (dateRange: { start: string; end: string }) => {
    try {
      isFinalizing.value = true;
      hasShownError.value = false;

      const destinationsFromSelectedCustomers = [
        ...new Set(selectedCustomers.value.map((c) => c.destination)),
      ];

      const payload: WeeklyBillingFinalizePayload = {
        customer: selectedCustomers.value.map((c) => c.customer.accountNumber),
        destination: destinationsFromSelectedCustomers,
        dateRange,
      };

      await reportsService.finalizeWeeklyBillingCharges(payload);

      toast.success('Charges finalized successfully');

      await Promise.all([fetchCustomers(dateRange), fetchDestinations(dateRange)]);
    } catch (error: any) {
      handleError(error, 'Failed to finalize charges');
    } finally {
      isFinalizing.value = false;
    }
  };

  const loadData = async (dateRange: { start: string; end: string }) => {
    hasShownError.value = false;
    await Promise.all([fetchCustomers(dateRange), fetchDestinations(dateRange)]);
  };

  const clearForm = () => {
    rawCustomers.value = [];
    customers.value = [];
    destinations.value = [];
    selectedCustomers.value = [];
    selectedDestinations.value = [];
  };

  return {
    rawCustomers,
    customers,
    filteredCustomers,
    destinations,
    selectedCustomers,
    selectedDestinations,
    searchInput,
    isLoadingCustomers,
    isLoadingDestinations,
    isDownloading,
    isFinalizing,
    hasSelectedData,

    fetchCustomers,
    fetchDestinations,
    downloadReport,
    finalizeCharges,
    loadData,
    clearForm,
  };
}
